import type { ElementType } from 'react'
import { Icons } from '@/components/ui/icons'

/**
 * The navigation of the Solicitation Command Center.
 *
 * There is exactly one array. There used to be two — `navItems` for the inherited
 * procurement surface and a second array beside it — picked at render time from a
 * build-target environment variable that defaulted to the wrong value, so a clean
 * checkout rendered the inherited product's rail. Owner Decision 1 settles the question:
 * this console has one navigation and it is the solicitation one.
 *
 * The inherited procurement surface (`/vendors`, `/invoices`, `/procurement/**`,
 * `/vendor-portal/**`, `/workbench`, goods receipt) **stays routed and leaves the
 * navigation**. Those routes still back data the solicitation flow reads — vendor
 * records carry UEI, CAGE and SAM registration — so they remain reachable when typed.
 * Their info text lives in `HIDDEN_ROUTE_INFO` below, because a route that leaves the
 * rail still deserves an explanation when someone lands on it.
 *
 * The Solicitation Workbench is deliberately absent: it is reached by clicking a row in
 * the Solicitation Explorer, never from the rail.
 */

/**
 * THE BUILD-TARGET SWITCH WAS DECLARED HERE AND IS GONE, which is the end of Decision 1's
 * second half.
 *
 * It survived as a compatibility shim while the Dashboard and the Agent Hub each still
 * opened by branching on it. Both were rewritten wholesale, the last consumer went with
 * the second of them, and the constant spent its final days referenced by nothing but its
 * own docstring. There is one product and one rail; a switch that can select a different
 * console is the defect that started all of this, and deleting it is what makes that
 * structural rather than conventional. Its name is deliberately not written here — the
 * definition of done greps this tree for it and expects no match.
 */

export interface NavItem {
  href: string
  label: string
  icon: ElementType
  description: string
  adminOnly?: boolean
  /** Reserved for an entry that is registered but must not render. */
  hidden?: boolean
}

export interface NavSection {
  title: string
  items: NavItem[]
}

/**
 * Decision 1's ten entries, in five groups. Two are `adminOnly`, so a non-admin
 * presenter sees eight.
 */
export const navItems: NavSection[] = [
  {
    title: 'Command Center',
    items: [
      {
        href: '/',
        label: 'Dashboard',
        icon: Icons.dashboard,
        description:
          'What needs a person now, where the work sits across the eighteen stages, and what the AI Employee holds. Every figure links to the screen that owns it.',
      },
      {
        href: '/solicitations',
        label: 'Solicitation Explorer',
        icon: Icons.clipboardList,
        description:
          'Every solicitation across the eighteen-stage federal lifecycle, with its current stage, gate state, ceiling value and open exception count. Open one to reach the Workbench.',
      },
    ],
  },
  {
    title: 'The AI Employee',
    items: [
      {
        href: '/agent-hub',
        label: 'Agent Hub',
        icon: Icons.network,
        description:
          'Six orchestrators — one per phase plus Governance — and the AI Operators beneath them, with capabilities, governing Auto Policies and measured success rate.',
      },
      {
        href: '/ai/policies',
        label: 'Auto Policies',
        icon: Icons.shield,
        description:
          'The FAR-derived rules in force, the authority each derives from, and what an Operator sees when one fires.',
      },
      {
        href: '/ai/insights',
        label: 'Auto Insights',
        icon: Icons.barChart,
        description:
          'What is happening across live solicitations, and what the AI Employee did: how often it acted, escalated, or declined to fill a gap.',
      },
    ],
  },
  {
    title: 'Contracts',
    items: [
      {
        href: '/contracts',
        label: 'Contract Hub',
        icon: Icons.scrollText,
        description:
          'Awarded contracts, obligated funds, modifications and CPARS findings, in USD.',
      },
    ],
  },
  {
    title: 'Administration',
    items: [
      {
        href: '/data-manager',
        label: 'Data Manager',
        icon: Icons.table,
        description:
          'Connected federal sources — SAM.gov, GSA eBuy, FPDS-NG, PIEE/WAWF, DLA — with every entity held and its lineage in both directions.',
      },
      {
        href: '/admin/audit',
        label: 'Audit Trail',
        icon: Icons.fileText,
        adminOnly: true,
        description:
          'Every action, the identity that took it, and the authority it was exercised under.',
      },
      {
        href: '/admin/users',
        label: 'Users and Roles',
        icon: Icons.users,
        adminOnly: true,
        description:
          'Manage identities and the federal authorities they may exercise.',
      },
    ],
  },
  {
    title: 'System',
    items: [
      {
        href: '/settings',
        label: 'Settings',
        icon: Icons.settings,
        description:
          'Personal preferences, notification routing, theme, and account settings.',
      },
    ],
  },
]

/**
 * Routes that are reachable but not in the rail (Decision 1). Their info text lives here
 * so `PageInfoButton` renders a popover instead of nothing.
 *
 * Before this map existed `getNavItemByHref` searched one array, so every route outside
 * it returned `undefined` and every info button on those pages rendered `null` — the
 * descriptions were written and never read.
 */
export const HIDDEN_ROUTE_INFO: Record<
  string,
  Pick<NavItem, 'label' | 'description'>
> = {
  '/inbound': {
    label: 'Inbound',
    description:
      'Scope messages received, and the manual injection path for a message that arrives outside the monitored mailbox. No arrival channel is assumed.',
  },
  '/brand': {
    label: 'Brand & Design System',
    description:
      'The token reference: phase ramp, stage and gate states, priority and operator badges, chart palette in both modes.',
  },
  '/contracts/templates': {
    label: 'Templates & Clauses',
    description:
      'Contract templates, standard clauses and the FAR clause matrix source table.',
  },
  '/vendors': {
    label: 'Vendors',
    description:
      'Vendor and contractor records carrying UEI, CAGE, SAM registration status, NAICS and set-aside certifications.',
  },
  '/vendors/onboarding': {
    label: 'Vendor Onboarding',
    description:
      'Vendor registration in progress: representations and certifications, SAM lookup, and the responsibility determination under FAR Part 9.',
  },
  '/vendor-portal': {
    label: 'Vendor Portal',
    description:
      'The token-addressed surface a vendor sees when invited to respond. Renders outside the console shell.',
  },
  '/invoices': {
    label: 'Invoices',
    description:
      'Invoices received against awarded contracts, matched to their orders and receipts. Inherited surface, retained because the contract record reads from it.',
  },
  '/procurement': {
    label: 'Purchasing',
    description:
      'The inherited purchasing surface. Retained because vendor, order and receipt records back the solicitation flow; not part of the eighteen-stage lifecycle.',
  },
  '/procurement/requests': {
    label: 'Requisitions',
    description:
      'Purchase requisitions raised on the inherited surface. Federal requirements enter through Inbound and the Solicitation Explorer instead.',
  },
  '/procurement/approvals': {
    label: 'My Approvals',
    description:
      'The inherited approvals queue. Federal approvals live on the Dashboard and on the Workbench gate panel.',
  },
  '/workbench': {
    label: 'Requisition Board',
    description:
      'The inherited purchase-requisition workflow board. Not part of the solicitation lifecycle.',
  },
  '/admin/roles': {
    label: 'Role Management',
    description:
      'Identity-provider roles. Federal authority titles live in the authority register, not here — a role name is not an authority.',
  },
  '/admin/groups': {
    label: 'Group Management',
    description: 'Identity-provider groups used to organise access.',
  },
  '/admin/sessions': {
    label: 'User Sessions',
    description: 'Active sign-in sessions and their connectivity.',
  },
  '/admin/events': {
    label: 'Login Events',
    description:
      'Sign-in activity from the identity provider. Distinct from the Audit Trail, which records actions and the authority they were taken under.',
  },
  '/admin/settings': {
    label: 'Admin Settings',
    description: 'Platform-wide configuration and administration.',
  },
}

/**
 * The label and description for a route, whether or not it is in the rail.
 *
 * Falls through to `HIDDEN_ROUTE_INFO` so the twenty-odd routed-but-hidden surfaces keep
 * their info popover.
 */
export function getNavItemByHref(
  href: string
): Pick<NavItem, 'label' | 'description'> | undefined {
  for (const section of navItems) {
    const item = section.items.find((navItem) => navItem.href === href)
    if (item) return item
  }
  return HIDDEN_ROUTE_INFO[href]
}
